// The tabs open in the agent pane beside every page, conversations and agent peeks, held
// above the routes so they outlive navigation. It also starts each run, so a start in
// flight completes and is recorded even if the page that asked for it has gone.
// See docs/Y2026-Q4/design-agent-side-pane.md.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import type { HeldAttachments } from "../api/hooks/useAttachments";

import {
  useStartStatefulRun,
} from "../api/hooks/useStatefulAgents";
import {
  type AgentPane,
  AgentPaneContext,
  NO_START,
  type PaneConversation,
  type PaneItem,
  type PaneRequest,
  type PaneStart,
  type PeekRequest,
  paneKey,
  peekKey,
} from "./useAgentPane";

export const AgentPaneProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useKindeAuth();
  const { mutateAsync: startRun } = useStartStatefulRun();
  const [items, setItems] = useState<PaneItem[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [minimised, setMinimised] = useState(false);
  const [runs, setRuns] = useState<Record<string, string>>({});
  const [starts, setStarts] = useState<Record<string, PaneStart>>({});
  const [working, setWorkingState] = useState<Record<string, boolean>>({});
  const setWorking = useCallback(
    (key: string, on: boolean) =>
      setWorkingState((all) => (all[key] === on ? all : { ...all, [key]: on })),
    [],
  );
  // Read synchronously, so two clicks in one tick cannot both start a run.
  const inFlight = useRef(new Set<string>());

  const open = useCallback((request: PaneRequest) => {
    const key = paneKey(request.projectId, request.config, request.actionId, request.intent);
    setItems((open) => {
      const existing = open.find((c) => c.key === key);
      if (!existing) return [...open, { ...request, tab: "conversation", key }];
      return open.map((c) =>
        c.key === key && c.tab === "conversation"
          ? {
              ...c,
              runId: c.runId ?? request.runId,
              email: request.email ?? c.email,
              context: request.context,
            }
          : c,
      );
    });
    setActiveKey(key);
    setMinimised(false);
  }, []);

  const peek = useCallback((request: PeekRequest) => {
    const key = peekKey(request);
    setItems((open) =>
      open.some((c) => c.key === key) ? open : [...open, { ...request, tab: "peek", key }],
    );
    setActiveKey(key);
    setMinimised(false);
  }, []);

  const close = useCallback(
    (key: string) => {
      const rest = items.filter((c) => c.key !== key);
      setItems(rest);
      // A failed start's error is not shown again on reopening; one in flight stays.
      setStarts((all) => {
        if (!all[key] || all[key].pending) return all;
        const others = { ...all };
        delete others[key];
        return others;
      });
      if (activeKey === key) setActiveKey(rest[rest.length - 1]?.key ?? null);
    },
    [items, activeKey],
  );

  const runOf = useCallback(
    (c: PaneConversation) => c.runId ?? runs[c.key] ?? null,
    [runs],
  );

  const start = useCallback(
    (c: PaneConversation, first?: { content: string } & HeldAttachments) => {
      if (runOf(c) || inFlight.current.has(c.key)) return;
      inFlight.current.add(c.key);
      setStarts((all) => ({ ...all, [c.key]: { pending: true, error: null } }));
      startRun({
        project_id: c.projectId,
        config: c.config,
        agent: c.agent,
        actor_email: user?.email ?? "",
        action: { action_id: c.actionId, intent: c.intent },
        ...(first ?? {}),
      })
        .then(
          (launched) => {
            setRuns((all) => ({ ...all, [c.key]: launched.run_id }));
            setStarts((all) => ({ ...all, [c.key]: NO_START }));
          },
          (e: Error) =>
            setStarts((all) => ({
              ...all,
              [c.key]: { pending: false, error: e.message },
            })),
        )
        .finally(() => inFlight.current.delete(c.key));
    },
    [runOf, startRun, user?.email],
  );

  const value = useMemo<AgentPane>(
    () => ({
      items,
      activeKey,
      minimised,
      open,
      peek,
      close,
      select: (key) => {
        setActiveKey(key);
        setMinimised(false);
      },
      setMinimised,
      startedRun: (projectId, config, actionId, intent) =>
        runs[paneKey(projectId, config, actionId, intent)] ?? null,
      runOf,
      startOf: (key) => starts[key] ?? NO_START,
      workingIn: (key) =>
        (starts[key] ?? NO_START).pending || Boolean(working[key]),
      setWorking,
      start,
    }),
    [
      items,
      activeKey,
      minimised,
      open,
      peek,
      close,
      runs,
      runOf,
      starts,
      working,
      setWorking,
      start,
    ],
  );

  return (
    <AgentPaneContext.Provider value={value}>
      {children}
    </AgentPaneContext.Provider>
  );
};
